import { ref, computed } from 'vue'

export function useConnectionQuality(options = {}) {
    const {
        poorLatencyMs = 1500,
        staleHeartbeatMs = 12000,
        checkIntervalMs = 3000,
    } = options

    const connectionState = ref(navigator.onLine ? 'connected' : 'offline')
    const connectionLatency = ref(null)
    const connectionBars = computed(() => {
        if (connectionState.value === 'offline') {
            return 0
        }

        if (connectionState.value === 'reconnecting') {
            return 1
        }

        const latency = connectionLatency.value

        if (latency) {
            if (latency < 250) return 4
            if (latency < 700) return 3
            if (latency < poorLatencyMs) return 2
            return 1
        }

        const lastSeen = lastPongAt.value || lastActivityAt.value

        if (lastSeen) {
            const staleFor = nowTick.value - lastSeen

            if (staleFor > staleHeartbeatMs) {
                return connectionState.value === 'poor' ? 2 : 3
            }

            if (staleFor > staleHeartbeatMs / 2) {
                return 3
            }
        }

        const info = networkInfo.value

        if (info) {
            if (info.effectiveType === 'slow-2g') return 1
            if (info.effectiveType === '2g') return 1
            if (info.effectiveType === '3g') return 2

            if (info.rtt && info.rtt > 1500) return 1
            if (info.rtt && info.rtt > 700) return 2
            if (info.rtt && info.rtt > 300) return 3

            if (info.downlink && info.downlink < 0.3) return 1
            if (info.downlink && info.downlink < 0.8) return 2
            if (info.downlink && info.downlink < 2) return 3
        }

        if (connectionState.value === 'poor') {
            return 2
        }

        return 4
    })

    const connectionLabel = computed(() => {
        switch (connectionBars.value) {
            case 0:
                return 'Offline'
            case 1:
                return connectionState.value === 'reconnecting'
                    ? 'Reconnecting'
                    : 'Very weak connection'
            case 2:
                return 'Weak connection'
            case 3:
                return 'Fair connection'
            case 4:
                return 'Good connection'
            default:
                return 'Connection unknown'
        }
    })

    const lastPingAt = ref(null)
    const lastPongAt = ref(null)
    const lastActivityAt = ref(null)
    const disconnectReason = ref(null)
    const networkInfo = ref(null)
    const nowTick = ref(Date.now())

    const socketRef = ref(null)
    const staleTimer = ref(null)

    let onSocketConnect = null
    let onSocketDisconnect = null
    let onSocketConnectError = null

    let onReconnectAttempt = null
    let onReconnect = null
    let onReconnectFailed = null

    let onEnginePacket = null

    const isConnected = computed(() => connectionState.value === 'connected')
    const isPoorConnection = computed(() => connectionState.value === 'poor')
    const isReconnecting = computed(() => connectionState.value === 'reconnecting')
    const isOffline = computed(() => connectionState.value === 'offline')

    function getNetworkConnection() {
        return navigator.connection || navigator.mozConnection || navigator.webkitConnection || null
    }

    function getNetworkInfo() {
        const connection = getNetworkConnection()

        if (!connection) {
            return null
        }

        return {
            effectiveType: connection.effectiveType,
            rtt: connection.rtt,
            downlink: connection.downlink,
            saveData: connection.saveData,
        }
    }

    function updateNetworkInfo() {
        networkInfo.value = getNetworkInfo()
    }

    function setState(nextState) {
        connectionState.value = nextState
    }

    function markActivity() {
        lastActivityAt.value = Date.now()
    }

    function updateStateBySocket() {
        const socket = socketRef.value

        if (!navigator.onLine) {
            setState('offline')
            return
        }

        if (!socket || !socket.connected) {
            setState('reconnecting')
            return
        }

        const latency = connectionLatency.value

        if (latency && latency > poorLatencyMs) {
            setState('poor')
            return
        }

        const lastSeen = lastPongAt.value || lastActivityAt.value

        if (lastSeen && Date.now() - lastSeen > staleHeartbeatMs) {
            if (connectionLatency.value && connectionLatency.value > poorLatencyMs) {
                setState('poor')
                return
            }

            setState('connected')
            return
        }

        const info = networkInfo.value

        if (
            info &&
            (
                info.effectiveType === 'slow-2g' ||
                info.effectiveType === '2g' ||
                (info.rtt && info.rtt > poorLatencyMs) ||
                (info.downlink && info.downlink < 0.3)
            )
        ) {
            setState('poor')
            return
        }

        setState('connected')
    }

    function bindEngineHeartbeat(socket) {
        const engine = socket?.io?.engine

        if (!engine) {
            return
        }

        unbindEngineHeartbeat(socket)

        onEnginePacket = (packet) => {
            markActivity()

            if (packet.type === 'ping') {
                lastPingAt.value = Date.now()
                return
            }

            if (packet.type === 'pong') {
                lastPongAt.value = Date.now()

                if (lastPingAt.value) {
                    connectionLatency.value = lastPongAt.value - lastPingAt.value
                }

                updateStateBySocket()
            }
        }

        engine.on('packet', onEnginePacket)
    }

    function unbindEngineHeartbeat(socket) {
        const engine = socket?.io?.engine

        if (engine && onEnginePacket) {
            engine.off('packet', onEnginePacket)
        }

        onEnginePacket = null
    }

    function bindSocket(socket) {
        unbindSocket()

        socketRef.value = socket

        onSocketConnect = () => {
            disconnectReason.value = null
            markActivity()
            updateNetworkInfo()
            bindEngineHeartbeat(socket)
            updateStateBySocket()
        }

        onSocketDisconnect = (reason) => {
            disconnectReason.value = reason
            connectionLatency.value = null
            setState(navigator.onLine ? 'reconnecting' : 'offline')
            unbindEngineHeartbeat(socket)
        }

        onSocketConnectError = (error) => {
            disconnectReason.value = error?.message || 'connect_error'
            connectionLatency.value = null
            setState(navigator.onLine ? 'reconnecting' : 'offline')
        }

        onReconnectAttempt = () => {
            connectionLatency.value = null
            setState(navigator.onLine ? 'reconnecting' : 'offline')
        }

        onReconnect = () => {
            disconnectReason.value = null
            markActivity()
            updateNetworkInfo()
            bindEngineHeartbeat(socket)
            updateStateBySocket()
        }

        onReconnectFailed = () => {
            connectionLatency.value = null
            setState(navigator.onLine ? 'reconnecting' : 'offline')
        }

        socket.on('connect', onSocketConnect)
        socket.on('disconnect', onSocketDisconnect)
        socket.on('connect_error', onSocketConnectError)

        if (socket.io) {
            socket.io.on('reconnect_attempt', onReconnectAttempt)
            socket.io.on('reconnect', onReconnect)
            socket.io.on('reconnect_failed', onReconnectFailed)
        }

        if (socket.connected) {
            bindEngineHeartbeat(socket)
        }

        updateNetworkInfo()
        updateStateBySocket()
        startStaleHeartbeatCheck()
    }

    function unbindSocket() {
        const socket = socketRef.value

        if (!socket) {
            stopStaleHeartbeatCheck()
            return
        }

        socket.off('connect', onSocketConnect)
        socket.off('disconnect', onSocketDisconnect)
        socket.off('connect_error', onSocketConnectError)

        if (socket.io) {
            socket.io.off('reconnect_attempt', onReconnectAttempt)
            socket.io.off('reconnect', onReconnect)
            socket.io.off('reconnect_failed', onReconnectFailed)
        }

        unbindEngineHeartbeat(socket)

        socketRef.value = null
        stopStaleHeartbeatCheck()
    }

    function startStaleHeartbeatCheck() {
        stopStaleHeartbeatCheck()

        staleTimer.value = setInterval(() => {
            nowTick.value = Date.now()
            updateNetworkInfo()

            const socket = socketRef.value

            if (!navigator.onLine) {
                setState('offline')
                return
            }

            if (!socket || !socket.connected) {
                setState('reconnecting')
                return
            }

            const lastSeen = lastPongAt.value || lastActivityAt.value

            if (!lastSeen) {
                updateStateBySocket()
                return
            }

            const staleFor = Date.now() - lastSeen

            if (staleFor > staleHeartbeatMs) {
                setState('poor')
                return
            }

            updateStateBySocket()
        }, checkIntervalMs)
    }

    function stopStaleHeartbeatCheck() {
        if (staleTimer.value) {
            clearInterval(staleTimer.value)
            staleTimer.value = null
        }
    }

    function handleOnline() {
        updateNetworkInfo()
        updateStateBySocket()
    }

    function handleOffline() {
        connectionLatency.value = null
        setState('offline')
    }

    function setupBrowserListeners() {
        window.addEventListener('online', handleOnline)
        window.addEventListener('offline', handleOffline)

        const connection = getNetworkConnection()

        if (connection) {
            connection.addEventListener('change', handleNetworkConnectionChange)
        }

        updateNetworkInfo()
        updateStateBySocket()
    }

    function removeBrowserListeners() {
        window.removeEventListener('online', handleOnline)
        window.removeEventListener('offline', handleOffline)

        const connection = getNetworkConnection()

        if (connection) {
            connection.removeEventListener('change', handleNetworkConnectionChange)
        }
    }

    function handleNetworkConnectionChange() {
        updateNetworkInfo()
        updateStateBySocket()
    }

    return {
        connectionState,
        connectionLatency,
        connectionBars,
        connectionLabel,
        connectionDisconnectReason: disconnectReason,

        lastPingAt,
        lastPongAt,
        lastActivityAt,
        disconnectReason,
        networkInfo,

        isConnected,
        isPoorConnection,
        isReconnecting,
        isOffline,

        bindSocket,
        unbindSocket,
        setupBrowserListeners,
        removeBrowserListeners,
        markActivity,
        updateStateBySocket,
        updateNetworkInfo,
    }
}